import {FileUpload, Loader} from '@/components/atoms'
import {useBulkPromotion} from '@/hooks/promotion/useBulkPromotion'
import {useCSVFileValidation} from '@/hooks/useCSVFileValidation-hook'
import {useFileUpload} from '@/hooks/useFileUpload'
import {INVENTORY_UPLOAD_HISTORY_KEY} from '@/hooks/useUploadHistory'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {Constants, NUMBERS} from '@/utils/constants/commonConstants'
import {useQueryClient} from '@tanstack/react-query'
import {forwardRef, useImperativeHandle, useState} from 'react'

const MAX_FILE_SIZE_MB = 10
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024

const UploadFile = forwardRef(
  (
    {
      acceptedExtensions,
      description,
      label,
      files,
      multiple = false,
      className = '',
      containerClassName = '',
      required = false,
      variant,
      buttonLabel = Constants.UploadFile,
      type,
      imageUri = '',
      isDark = true,
      onUpload = () => {},
      feedbackType,
      onUploadDelete = () => {},
      helperText = '',
      onUploadError = () => {},
      fileSizeCheck = false,
      dataTestId = '',
      disabled = false,
      uploadFor,
      isCsvValidationRequired = false,
      isTemplateUpload = false,
      onCloseModal = () => {},
      toastMessage = 'File has successfully been uploaded and the status can be checked in Upload History',
    },
    ref
  ) => {
    const [uploadedFile, setUploadedFile] = useState({})
    const {uploadFile, getFileUploadDetails} = useFileUpload()
    const {validateCSV} = useCSVFileValidation()
    const {handleBulkPromotionFile} = useBulkPromotion(uploadedFile)
    const [fileFeedback, setFileFeedback] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const dispatch = useAppDispatch()
    const queryClient = useQueryClient()

    /** @param {string | string[]} errorMessage */
    const setFeedbackError = (errorMessage, feedbackType = Constants.Error) => {
      const message = errorMessage || Constants.ErrorForFileUpload
      setFileFeedback({feedbackType, helperText: message})
      if (feedbackType === Constants.Error) {
        onUploadError(true)
      }
    }

    const clearFeedback = () => {
      setFileFeedback(null)
      onUploadError(false)
    }

    const csvValidation = file => {
      validateCSV(file, uploadFor, ({data, errors}) => {
        setTotalCount(data?.length)
        if (errors.length > 0) {
          onUploadError(true)
          setFeedbackError(errors)
        } else {
          setUploadedFile(file)
          onUpload(file)
          clearFeedback()
        }
        setUploading(false)
      })
    }

    useImperativeHandle(ref, () => ({
      fileUpload: async () => {
        setUploading(true)
        await getFileUploadDetails({
          file: uploadedFile,
          uploadFor,
          isTemplateUpload,
          totalCount,
          onSuccess: () => {
            onUpload(uploadedFile)
            clearFeedback()
            dispatch(
              setToastNotifications({
                show: true,
                message: toastMessage,
                type: 'success',
              })
            )
            onCloseModal?.()
            queryClient.invalidateQueries({queryKey: [INVENTORY_UPLOAD_HISTORY_KEY]})
          },
          onError: setFeedbackError,
        })
        setUploading(false)
      },
      bulkPromotionFileUpload: () => {
        handleBulkPromotionFile(onCloseModal, setUploading, setFeedbackError, clearFeedback)
      },
      clearFeedback,
    }))

    const handleDelete = () => {
      clearFeedback()
      onUploadDelete()
    }

    const fileUpload = async file => {
      try {
        const res = await uploadFile(file, uploadFor, isTemplateUpload)

        if (!res || !res.success || Object.keys(res).length === 0) {
          setFeedbackError(res?.message)
          return
        }

        onUpload(file)
        clearFeedback()
      } catch (err) {
        setFeedbackError(`Error verifying columns: ${err.message}`)
      } finally {
        setUploading(false)
      }
    }

    const handleUpload = async file => {
      clearFeedback()
      setUploading(true)

      if (file?.type === 'text/csv' && isCsvValidationRequired) return csvValidation(file)

      if (acceptedExtensions === Constants.Xlsx) {
        if (file?.size > MAX_FILE_SIZE) {
          setFeedbackError(`File size should be below ${MAX_FILE_SIZE_MB}MB`)
          setUploading(false)
          return
        }
        setUploading(false)
        onUpload(file)
        setUploadedFile(file)
        return
      }

      if (fileSizeCheck) {
        const fileSizeMB = file.size / (1024 * 1024)
        if (fileSizeMB > NUMBERS.Fifty) {
          setFeedbackError('File cannot be uploaded. File size should be below 50MB')
          setUploading(false)
          return
        }
      }

      await fileUpload(file)
    }

    const getFeedbackType = () => (fileFeedback ? fileFeedback.feedbackType : feedbackType)
    const getHelperText = () => {
      const text = fileFeedback?.helperText || helperText
      if (Array.isArray(text)) {
        return (
          <div>
            {text.map((err, index) => (
              <p key={index}>{err}</p>
            ))}
          </div>
        )
      }
      return text
    }

    const isButtonDisabled = disabled || uploading

    return (
      <>
        {uploading && <Loader />}
        <FileUpload
          acceptedExtensions={acceptedExtensions}
          description={description}
          label={label}
          variant={uploading ? Constants.Uploading : variant}
          buttonLabel={buttonLabel}
          onUpload={handleUpload}
          onUploadDelete={handleDelete}
          type={type}
          required={required}
          imageUri={imageUri}
          containerClassName={`${!fileFeedback && containerClassName} ${fileFeedback?.feedbackType === Constants.Error ? '!border-2 !border-dotted !border-red-500' : ''}`}
          className={className}
          multiple={multiple}
          value={fileFeedback?.feedbackType === Constants.Error ? '' : files}
          isDark={isDark}
          feedbackType={getFeedbackType()}
          helperText={getHelperText()}
          onUploadError={() => {
            setFeedbackError()
          }}
          dataTestId={dataTestId}
          disabled={isButtonDisabled}
        />
      </>
    )
  }
)

UploadFile.displayName = 'UploadFile'

export default UploadFile
